import { useCallback, useEffect, useState } from 'react';
import { computeProgress } from './onboarding.hook';
import { onboardingApi } from './onboarding.api';

/**
 * Controls the OnboardingCompletionModal.
 *
 * The modal is shown at most once per user, and only on a fresh page
 * load / navigation — never mid-session immediately after the last action
 * completes. This preserves the user's current flow.
 *
 * Activation conditions (both must hold at the time of page mount):
 *   - computeProgress(state) === 100
 *   - state.completionCelebrationSeen === false
 *
 * Usage:
 *   const { showModal, markCelebrationSeen } = useOnboardingCompletion();
 *   <OnboardingCompletionModal
 *     isOpen={showModal}
 *     markCelebrationSeen={markCelebrationSeen}
 *     onClose={...}
 *     onOpenDashboard={...}
 *     onShareAnyHomes={...}
 *   />
 */
export function useOnboardingCompletion() {
  const [showModal, setShowModal] = useState(false);

  // Intentional single-run on mount: we snapshot the backend state at page-load
  // time and do NOT react to subsequent state changes during the session.
  // This ensures the modal never pops up mid-action — the user sees it only
  // on the next page load / reconnect after reaching 100 %.
  useEffect(() => {
    onboardingApi.getState().then((mountState) => {
      const progress = computeProgress(mountState);
      if (progress === 100 && !mountState.completionCelebrationSeen) {
        setShowModal(true);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Closes the modal optimistically, then persists the flag to the backend.
   * Safe to call multiple times; subsequent calls are no-ops once the modal
   * is already closed.
   */
  const markCelebrationSeen = useCallback(async () => {
    setShowModal(false);
    await onboardingApi.markCelebrationSeen();
  }, []);

  return { showModal, markCelebrationSeen };
}
